import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const floatUp = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const BalanceCard = styled.div`
  background: rgba(255, 255, 255, 0.025);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 2rem;
  position: relative;
  overflow: hidden;
  animation: ${floatUp} 0.6s ease-out;
`;

const BalanceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const BalanceTitle = styled.h3`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0;
`;

const RefreshButton = styled.button`
  background: #3B82F6;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-family: 'Very Sans Bold', sans-serif;
  font-weight: 600;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: #2563EB;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const BalanceDisplay = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
  padding: 2rem 1.5rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const BalanceAmount = styled.div`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 2.5rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 0.5rem;
  line-height: 1;
`;

const BalanceLabel = styled.div`
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  color: #94A3B8;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const TransactionsSection = styled.div`
  margin-top: 1.5rem;
`;

const SectionTitle = styled.h4`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.95rem;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TransactionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const TransactionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(59, 130, 246, 0.3);
    transform: translateY(-1px);
  }
`;

const TransactionLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const TransactionService = styled.div`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #ffffff;
`;

const TransactionDate = styled.div`
  font-family: 'Very Sans Medium', sans-serif;
  font-size: 0.75rem;
  font-weight: 500;
  color: #94A3B8;
`;

const TransactionRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`;

const TransactionAmount = styled.div`
  font-family: 'Very Sans Bold', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: #22c55e;
`;

const TransactionHash = styled.div`
  font-family: 'Courier New', monospace;
  font-size: 0.7rem;
  color: #94A3B8;
  cursor: pointer;
  transition: color 0.2s ease;
  
  &:hover {
    color: #3B82F6;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(59, 130, 246, 0.1);
  border-left: 3px solid #3B82F6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 2rem auto;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const ErrorMessage = styled.div`
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
  padding: 1rem;
  border-radius: 8px;
  text-align: center;
  font-family: 'Very Sans Medium', sans-serif;
  font-weight: 500;
  font-size: 0.9rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 2rem 1rem;
  color: #94A3B8;
  font-family: 'Very Sans Medium', sans-serif;
  font-weight: 500;
  font-size: 0.9rem;
`;

const CardBalance = ({ cardId }) => {
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchCardBalance = async () => {
    try {
      setError(null);
      console.log('🔍 Fetching balance for card:', cardId);
      
      const url = `https://chainfree.info:7000/card-details/${cardId}?limit=3`;
      console.log('🔍 API URL:', url);
      
      const response = await axios.get(url);
      console.log('🔍 Response status:', response.status);
      console.log('🔍 Response data:', response.data);
      
      const data = response.data;
      
      if (data.success) {
        setBalance(data.balance);
        setTransactions(data.recent_transactions);
        console.log('✅ Balance loaded successfully');
      } else {
        setError(data.error || 'Failed to fetch balance');
        console.error('❌ API returned error:', data.error);
      }
    } catch (err) {
      console.error('❌ Axios error:', err);
      
      if (err.response) {
        setError(`Server error: ${err.response.status} - ${err.response.data?.error || err.response.statusText}`);
      } else if (err.request) {
        setError('No response from server. Check if server is running.');
      } else {
        setError(`Request error: ${err.message}`);
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchCardBalance();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const truncateHash = (hash) => {
    if (!hash) return 'N/A';
    return `${hash.slice(0, 8)}...${hash.slice(-8)}`;
  };

  const openTransaction = (hash) => {
    if (hash && hash !== 'N/A') {
      window.open(`https://sepolia.etherscan.io/tx/${hash}`, '_blank');
    }
  };

  useEffect(() => {
    console.log('🔍 CardBalance component mounted with cardId:', cardId);
    if (cardId) {
      fetchCardBalance();
    } else {
      console.warn('⚠️ No cardId provided to CardBalance component');
      setError('No card ID provided');
      setLoading(false);
    }
  }, [cardId]);

  if (loading) {
    return (
      <BalanceCard>
        <BalanceTitle>Card Balance</BalanceTitle>
        <LoadingSpinner />
      </BalanceCard>
    );
  }

  if (error) {
    return (
      <BalanceCard>
        <BalanceHeader>
          <BalanceTitle>Card Balance</BalanceTitle>
          <RefreshButton onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? 'Refreshing...' : 'Retry'}
          </RefreshButton>
        </BalanceHeader>
        <ErrorMessage>{error}</ErrorMessage>
      </BalanceCard>
    );
  }

  return (
    <BalanceCard>
      <BalanceHeader>
        <BalanceTitle>Card Balance</BalanceTitle>
        <RefreshButton onClick={handleRefresh} disabled={refreshing}>
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </RefreshButton>
      </BalanceHeader>

      <BalanceDisplay>
        <BalanceAmount>${balance?.total_balance_usd?.toFixed(2) || '0.00'}</BalanceAmount>
        <BalanceLabel>Available Balance</BalanceLabel>
      </BalanceDisplay>

      <TransactionsSection>
        <SectionTitle>Recent Fundings</SectionTitle>
        {transactions.length > 0 ? (
          <TransactionList>
            {transactions.map((transaction) => (
              <TransactionItem key={transaction.id}>
                <TransactionLeft>
                  <TransactionService>
                    {transaction.subscription_name || 'Payment'}
                  </TransactionService>
                  <TransactionDate>
                    {formatDate(transaction.created_at)}
                  </TransactionDate>
                </TransactionLeft>
                <TransactionRight>
                  <TransactionAmount>
                    +${transaction.usdc_amount}
                  </TransactionAmount>
                  <TransactionHash 
                    onClick={() => openTransaction(transaction.transaction_hash)}
                    title="View on blockchain explorer"
                  >
                    {truncateHash(transaction.transaction_hash)}
                  </TransactionHash>
                </TransactionRight>
              </TransactionItem>
            ))}
          </TransactionList>
        ) : (
          <EmptyState>
            No funding transactions yet
          </EmptyState>
        )}
      </TransactionsSection>
    </BalanceCard>
  );
};

export default CardBalance;